import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import MapContainer from '../components/map/MapContainer';
import MapContextMenu from '../components/map/MapContextMenu';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import { PHASES, PHASE_COLOR } from '../components/opportunities/PhaseTimeline';
import { Target, Map, LayoutGrid, Plus, Search, RefreshCw } from 'lucide-react';

const PHASE_LABELS = Object.fromEntries(PHASES.map(p => [p.key, p.label]));

const OpportunitiesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // 'kanban' | 'map'
  const [searchText, setSearchText] = useState('');
  const [contextMenu, setContextMenu] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${getApiUrl()}/opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter only manual (user-created) opportunities
      setOpportunities((data || []).filter(o => o.source === 'manual'));
    } catch (e) {
      console.error('Error fetching opportunities', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  const handleAdvancePhase = async (id, newPhase) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${id}/phase`,
        { phase: newPhase },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpportunities(prev => prev.map(o => o._id === id ? data : o));
    } catch (e) {
      console.error('Error advancing phase', e);
    }
  };

  const handleMapDblClick = (location) => {
    setContextMenu({
      position: { x: location.x, y: location.y },
      coords: { lat: location.lat, lng: location.lng },
    });
  };

  const filtered = opportunities.filter(o =>
    !searchText || o.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // For map view: add phase-based color override
  const mapOpportunities = filtered.map(o => ({
    ...o,
    visuals: {
      ...o.visuals,
      color: PHASE_COLOR[o.phase] || '#E27B58',
    },
  }));

  const total = opportunities.length;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#05070A',
      color: '#F0F0F0',
      overflow: 'hidden',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        padding: '14px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
        background: 'rgba(5,7,10,0.98)',
      }}>
        <Target size={20} color="#E27B58" />
        <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '900', letterSpacing: '2px', textTransform: 'uppercase', color: '#E27B58' }}>
          Opportunities
          <span style={{ fontSize: '0.7rem', color: '#555', marginLeft: '10px', fontWeight: '400', letterSpacing: '1px' }}>
            {total} registradas
          </span>
        </h1>

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '6px 12px', flex: '1 1 150px', minWidth: 0 }}>
          <Search size={13} color="#666" />
          <input
            placeholder="Buscar..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ background: 'transparent', border: 'none', color: '#F0F0F0', outline: 'none', fontSize: '0.82rem', flex: 1, minWidth: 0, width: '100%' }}
          />
        </div>

        {/* View toggle */}
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          {[{ key: 'kanban', Icon: LayoutGrid, label: 'Kanban' }, { key: 'map', Icon: Map, label: 'Mapa' }].map(({ key, Icon, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              title={label}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px',
                background: view === key ? 'rgba(226,123,88,0.2)' : 'transparent',
                border: 'none',
                color: view === key ? '#E27B58' : '#666',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: view === key ? '700' : '400',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button onClick={fetchOpportunities} title="Recargar" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#666', cursor: 'pointer', padding: '6px 10px' }}>
          <RefreshCw size={13} />
        </button>

        {/* New */}
        <button
          onClick={() => navigate('/opportunities/new')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: '#E27B58', border: 'none', borderRadius: '6px',
            color: 'white', cursor: 'pointer', padding: '8px 16px',
            fontWeight: '700', fontSize: '0.82rem', letterSpacing: '0.5px',
          }}
        >
          <Plus size={13} /> Nueva
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontFamily: 'monospace', letterSpacing: '2px' }}>
          CARGANDO OPORTUNIDADES...
        </div>
      ) : (
        <>
          {view === 'kanban' && (
            <div style={{
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              display: 'flex',
              gap: '12px',
              padding: '16px 20px',
            }}>
              {PHASES.map(phase => {
                const phaseItems = filtered.filter(o => (o.phase || 'exploration') === phase.key);
                return (
                  <div
                    key={phase.key}
                    style={{
                      minWidth: 220,
                      maxWidth: 260,
                      flex: '1 0 220px',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255,255,255,0.015)',
                      border: `1px solid ${phase.color}22`,
                      borderTop: `3px solid ${phase.color}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Column header */}
                    <div style={{
                      padding: '10px 12px',
                      borderBottom: `1px solid ${phase.color}22`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: '700', color: phase.color, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        {phase.label}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: '#444', background: 'rgba(255,255,255,0.05)', padding: '1px 7px', borderRadius: '10px' }}>
                        {phaseItems.length}
                      </span>
                    </div>

                    {/* Cards */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 6px' }}>
                      {phaseItems.length === 0 ? (
                        <p style={{ fontSize: '0.72rem', color: '#333', textAlign: 'center', marginTop: '20px', fontStyle: 'italic' }}>
                          Sin oportunidades
                        </p>
                      ) : (
                        phaseItems.map(opp => (
                          <OpportunityCard
                            key={opp._id}
                            opportunity={opp}
                            onAdvancePhase={handleAdvancePhase}
                          />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === 'map' && (
            <div style={{ flex: 1, position: 'relative' }}>
              <MapContainer
                opportunities={mapOpportunities}
                onMapDblClick={handleMapDblClick}
                onMarkerClick={(opp) => navigate(`/opportunities/${opp._id}`)}
              />
              {contextMenu && (
                <MapContextMenu
                  position={contextMenu.position}
                  coords={contextMenu.coords}
                  onClose={() => setContextMenu(null)}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OpportunitiesPage;
